import { appConfig } from "../../config/appConfig.js";
import { BEHAVIORAL_CATEGORIES, TRIGGER_TYPES } from "../domain/types.js";

/**
 * Optional Layer 2b — Claude refines category + severity from Play metadata.
 * Set `EXPO_PUBLIC_ANTHROPIC_API_KEY` or `REACT_APP_ANTHROPIC_API_KEY` in `.env`.
 */

/**
 * @returns {string|null}
 */
export function getAnthropicApiKey() {
  const key = appConfig.anthropicApiKey;
  return key ? String(key).trim() : null;
}

export function isClaudeClassificationAvailable() {
  return !!getAnthropicApiKey();
}

/**
 * @typedef {Object} ClaudeClassificationResult
 * @property {import('../domain/types.js').BehavioralCategory} primaryCategory
 * @property {'clean'|'flagged'|'unknown'} contentSeverity
 * @property {import('../domain/types.js').TriggerType[]} triggerTypes
 * @property {number} confidence
 * @property {string} reason
 */

/**
 * @param {{
 *   displayName: string,
 *   packageName: string,
 *   playTitle?: string,
 *   playDescription?: string,
 *   playContentRating?: string,
 *   heuristicCategory?: string,
 *   heuristicSeverity?: string,
 * }} input
 * @returns {Promise<ClaudeClassificationResult|null>}
 */
export async function classifyWithClaude(input) {
  const apiKey = getAnthropicApiKey();
  if (!apiKey) return null;

  const prompt = buildClassificationPrompt(input);

  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: {
      "x-api-key": apiKey,
      "anthropic-version": "2023-06-01",
      "content-type": "application/json",
    },
    body: JSON.stringify({
      model: "claude-3-5-haiku-20241022",
      max_tokens: 400,
      temperature: 0,
      messages: [{ role: "user", content: prompt }],
    }),
  });

  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    throw new Error(`Claude API ${res.status}: ${errText.slice(0, 200)}`);
  }

  const data = await res.json();
  const text = data?.content?.find((c) => c.type === "text")?.text || "";
  return parseClaudeJson(text);
}

/**
 * @param {typeof input} input
 */
/** Map AI shorthand categories → Lockbox BehavioralCategory */
const AI_CATEGORY_MAP = {
  manga_reading: "manga_reading",
  anime_streaming: "anime_streaming",
  shortform_feed: "shortform_feed",
  work_comms: "work_comms",
  gaming: "gaming_session",
  education: "learning",
  entertainment: "entertainment_discovery",
  productivity: "productivity",
  other: "unknown",
};

function buildClassificationPrompt(input) {
  const appName = input.playTitle || input.displayName;
  const storeDescription = (input.playDescription || "").slice(0, 2000);
  const storeRating = input.playContentRating || "unknown";

  return `Given this app name, description, and content rating, classify its content severity.

App: ${appName}
Description: ${storeDescription}
Content Rating: ${storeRating}

Respond ONLY in JSON:
{
  "category": "manga_reading | anime_streaming | shortform_feed | work_comms | gaming | education | entertainment | productivity | news | shopping | other",
  "contentSeverity": "clean | flagged | unknown",
  "reason": "one sentence explanation"
}

Rules:
- "flagged" for explicit, sexual, hentai, ecchi, NSFW, gore, or mature webtoon/manga with adult themes.
- "clean" for messaging, navigation, banking, productivity without adult signals.
- Do not flag only because of Teen rating; read the description.
- WhatsApp → work_comms, clean. Toomics → manga_reading, flagged.`;
}

/**
 * @param {string} text
 * @returns {ClaudeClassificationResult|null}
 */
function parseClaudeJson(text) {
  const trimmed = String(text || "").trim();
  const jsonMatch = trimmed.match(/\{[\s\S]*\}/);
  if (!jsonMatch) return null;
  try {
    const o = JSON.parse(jsonMatch[0]);
    const rawCat = String(o.category || o.primaryCategory || "other").trim();
    const mapped = AI_CATEGORY_MAP[rawCat] || (BEHAVIORAL_CATEGORIES.includes(rawCat) ? rawCat : "unknown");
    const sev = o.contentSeverity === "clean" || o.contentSeverity === "flagged" ? o.contentSeverity : "unknown";
    const triggers =
      sev === "flagged"
        ? ["sexualized_content_risk", "escapism_media"]
        : mapped === "shortform_feed"
          ? ["compulsive_scroll"]
          : ["none"];
    return {
      primaryCategory: mapped,
      contentSeverity: sev,
      triggerTypes: triggers.filter((t) => TRIGGER_TYPES.includes(t)),
      confidence: 0.85,
      reason: String(o.reason || "AI classification"),
    };
  } catch {
    return null;
  }
}
